import { useEffect, useMemo, useState } from "react";
import { X, Plus, Trash, MagnifyingGlass, UserPlus, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const METHODS = [
  { value: "cash",    label: "Cash" },
  { value: "check",   label: "Check" },
  { value: "cashapp", label: "CashApp" },
  { value: "zelle",   label: "Zelle" },
  { value: "venmo",   label: "Venmo" },
  { value: "wire",    label: "Wire transfer" },
  { value: "ach",     label: "ACH" },
  { value: "paypal",  label: "PayPal" },
  { value: "other",   label: "Other" },
];

const blankLine = () => ({ description: "", quantity: 1, unit_price: "" });

/** Standalone receipt — records + emails a branded PDF for a payment already received off-platform. */
export function CreateReceiptModal({ open, onClose, onCreated }) {
  const [clients, setClients] = useState([]);
  const [clientMode, setClientMode] = useState("existing");
  const [search, setSearch] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [newClient, setNewClient] = useState({ name: "", email: "", company: "" });

  const [method, setMethod] = useState("cash");
  const [paidAt, setPaidAt] = useState(todayIso());
  const [referenceNo, setReferenceNo] = useState("");
  const [memo, setMemo] = useState("");
  const [lines, setLines] = useState([blankLine()]);
  const [sendImmediately, setSendImmediately] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMethod("cash"); setPaidAt(todayIso()); setReferenceNo(""); setMemo("");
    setLines([blankLine()]); setSendImmediately(true);
    setClientMode("existing"); setSearch(""); setSelectedClientId("");
    setNewClient({ name: "", email: "", company: "" });
    (async () => {
      try { const { data } = await api.get("/admin/clients"); setClients(data || []); }
      catch (_) { /* soft-fail */ }
    })();
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients.slice(0, 50);
    return clients.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.company_name || "").toLowerCase().includes(q)
    ).slice(0, 50);
  }, [clients, search]);

  const total = useMemo(
    () => lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0), 0),
    [lines]
  );

  if (!open) return null;

  const updateLine = (i, patch) => setLines((arr) => arr.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeLine = (i) => setLines((arr) => arr.length === 1 ? arr : arr.filter((_, idx) => idx !== i));
  const addLine = () => setLines((arr) => [...arr, blankLine()]);

  const validate = () => {
    if (clientMode === "existing" && !selectedClientId) return "Pick a client";
    if (clientMode === "new") {
      if (!newClient.name.trim()) return "Client name is required";
      if (!newClient.email.trim() || !newClient.email.includes("@")) return "Valid client email is required";
    }
    for (const [i, l] of lines.entries()) {
      if (!l.description.trim()) return `Line ${i + 1}: description required`;
      if (!(Number(l.quantity) > 0)) return `Line ${i + 1}: quantity must be > 0`;
      if (!(Number(l.unit_price) > 0)) return `Line ${i + 1}: amount must be > 0`;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v !== true) return toast.error(v);
    setSaving(true);
    try {
      const client = clientMode === "existing"
        ? { existing_client_id: selectedClientId }
        : {
            name: newClient.name.trim(),
            email: newClient.email.trim().toLowerCase(),
            company: newClient.company.trim() || null,
          };
      const payload = {
        client,
        payment_method: method,
        paid_at: paidAt || null,
        reference_no: referenceNo.trim() || null,
        memo: memo.trim() || null,
        send_immediately: sendImmediately,
        line_items: lines.map((l) => ({
          description: l.description.trim(),
          quantity: Number(l.quantity),
          unit_price: Number(l.unit_price),
        })),
      };
      const { data } = await api.post("/admin/receipts", payload);
      toast.success(sendImmediately
        ? `Receipt ${data.receipt_number} sent to ${data.client_email}`
        : `Receipt ${data.receipt_number} saved`);
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create receipt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 p-4 overflow-y-auto" data-testid="create-receipt-modal">
      <div className="bg-[#0a0a0a] border border-[#28C878] w-full max-w-3xl my-8">
        <div className="sticky top-0 z-10 flex items-center justify-between p-5 border-b border-[#262626] bg-[#0a0a0a]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#28C878]">New receipt</div>
            <div className="font-display text-2xl mt-1">Record & send</div>
          </div>
          <button onClick={onClose} className="p-1.5 text-[#a3a3a3] hover:text-[#FF2D2D]" aria-label="Close" data-testid="receipt-modal-close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-6">
          {/* Client */}
          <section>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] mb-2">Client</div>
            <div className="grid grid-cols-2 gap-px bg-[#262626] border border-[#262626] mb-3" role="radiogroup">
              <button type="button" onClick={() => setClientMode("existing")} data-testid="receipt-client-mode-existing"
                className={`px-3 py-2 text-xs uppercase tracking-wider transition-colors ${clientMode === "existing" ? "bg-[#28C878] text-black" : "bg-[#0a0a0a] text-[#a3a3a3] hover:bg-[#121212]"}`}>
                <MagnifyingGlass size={11} className="inline mr-1.5" weight="bold" /> Pick existing
              </button>
              <button type="button" onClick={() => setClientMode("new")} data-testid="receipt-client-mode-new"
                className={`px-3 py-2 text-xs uppercase tracking-wider transition-colors ${clientMode === "new" ? "bg-[#28C878] text-black" : "bg-[#0a0a0a] text-[#a3a3a3] hover:bg-[#121212]"}`}>
                <UserPlus size={11} className="inline mr-1.5" weight="bold" /> Create new
              </button>
            </div>

            {clientMode === "existing" ? (
              <div className="space-y-2">
                <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, email, company…" data-testid="receipt-client-search"
                  className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-sm text-white focus:border-[#28C878] outline-none" />
                <div className="max-h-48 overflow-y-auto border border-[#262626] divide-y divide-[#262626]" data-testid="receipt-client-list">
                  {filtered.length === 0 ? (
                    <div className="p-3 text-xs text-[#a3a3a3]">No matches.</div>
                  ) : filtered.map((c) => (
                    <label key={c.id} className={`flex items-center gap-3 p-2.5 cursor-pointer hover:bg-[#121212] ${selectedClientId === c.id ? "bg-[#28C878]/10 border-l-2 border-[#28C878]" : ""}`}>
                      <input type="radio" name="rcpt-client" value={c.id} checked={selectedClientId === c.id}
                        onChange={() => setSelectedClientId(c.id)} className="accent-[#28C878]"
                        data-testid={`receipt-pick-client-${c.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{c.name}</div>
                        <div className="text-[11px] text-[#a3a3a3] truncate">{c.email}{c.company_name ? ` · ${c.company_name}` : ""}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input label="Name *" value={newClient.name} onChange={(v) => setNewClient((c) => ({ ...c, name: v }))} testid="receipt-new-client-name" />
                <Input label="Email *" type="email" value={newClient.email} onChange={(v) => setNewClient((c) => ({ ...c, email: v }))} testid="receipt-new-client-email" />
                <Input label="Company" value={newClient.company} onChange={(v) => setNewClient((c) => ({ ...c, company: v }))} testid="receipt-new-client-company" />
              </div>
            )}
          </section>

          {/* Payment details */}
          <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Payment method *</label>
              <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="receipt-method"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#28C878] outline-none">
                {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Paid on *</label>
              <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} data-testid="receipt-paid-at"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#28C878] outline-none" />
            </div>
            <Input label="Reference # (optional)" value={referenceNo} onChange={setReferenceNo} testid="receipt-reference"
              placeholder="Check #, txn ID, CashApp tag…" />
          </section>

          {/* Line items */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3]">Line items</div>
              <button type="button" onClick={addLine} data-testid="receipt-add-line"
                className="text-[10px] uppercase tracking-wider text-[#28C878] hover:text-white flex items-center gap-1">
                <Plus size={11} weight="bold" /> Add line
              </button>
            </div>
            <div className="space-y-2">
              {lines.map((l, idx) => (
                <div key={idx} className="border border-[#262626] bg-[#050505] p-3" data-testid={`receipt-line-${idx}`}>
                  <div className="grid grid-cols-12 gap-2">
                    <input type="text" placeholder="Description" value={l.description}
                      onChange={(e) => updateLine(idx, { description: e.target.value })}
                      data-testid={`receipt-line-desc-${idx}`}
                      className="col-span-12 md:col-span-6 bg-[#0a0a0a] border border-[#262626] px-3 py-2 text-sm text-white focus:border-[#28C878] outline-none" />
                    <input type="number" step="0.01" min="0.01" placeholder="Qty" value={l.quantity}
                      onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                      data-testid={`receipt-line-qty-${idx}`}
                      className="col-span-4 md:col-span-2 bg-[#0a0a0a] border border-[#262626] px-3 py-2 text-sm text-white tabular-nums focus:border-[#28C878] outline-none" />
                    <div className="col-span-6 md:col-span-3 relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a3a3a3] text-sm">$</span>
                      <input type="number" step="0.01" min="0.01" placeholder="Amount" value={l.unit_price}
                        onChange={(e) => updateLine(idx, { unit_price: e.target.value })}
                        data-testid={`receipt-line-unit-${idx}`}
                        className="w-full bg-[#0a0a0a] border border-[#262626] pl-7 pr-3 py-2 text-sm text-white tabular-nums focus:border-[#28C878] outline-none" />
                    </div>
                    <button type="button" onClick={() => removeLine(idx)} title="Remove"
                      disabled={lines.length === 1}
                      data-testid={`receipt-remove-line-${idx}`}
                      className="col-span-2 md:col-span-1 border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D] disabled:opacity-30 disabled:cursor-not-allowed">
                      <Trash size={12} className="mx-auto" />
                    </button>
                  </div>
                  <div className="text-right text-xs text-[#a3a3a3] tabular-nums mt-2">
                    Line total: <span className="text-white">{formatUSD(Number(l.quantity || 0) * Number(l.unit_price || 0))}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Memo (optional, visible on receipt)</label>
            <textarea value={memo} onChange={(e) => setMemo(e.target.value)} rows={2}
              data-testid="receipt-memo" maxLength={1000}
              placeholder="Thanks for the fast payment — see you next month!"
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-sm text-white focus:border-[#28C878] outline-none" />
          </div>

          {/* Total */}
          <div className="border border-[#28C878] bg-[#28C878]/5 flex items-center justify-between px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#28C878]">Total received</div>
            <div className="font-display text-2xl text-white tabular-nums" data-testid="receipt-total">{formatUSD(total)}</div>
          </div>

          {/* Send toggle */}
          <label className="flex items-start gap-3 border border-[#262626] bg-[#050505] p-3 cursor-pointer">
            <input type="checkbox" checked={sendImmediately} onChange={(e) => setSendImmediately(e.target.checked)}
              className="accent-[#28C878] mt-0.5" data-testid="receipt-send-immediately" />
            <div className="flex-1">
              <div className="flex items-center gap-1.5 text-sm">
                <PaperPlaneTilt size={13} weight="fill" className="text-[#28C878]" />
                Email the receipt PDF now
              </div>
              <div className="text-[10px] text-[#a3a3a3] mt-0.5">
                Emails the branded PDF receipt to the client. Uncheck to record silently — you can resend later.
              </div>
            </div>
          </label>

          <div className="flex justify-end gap-3 pt-3 border-t border-[#262626]">
            <button type="button" onClick={onClose} data-testid="receipt-cancel"
              className="btn-outline px-4 py-2 text-xs uppercase tracking-wider">Cancel</button>
            <button type="submit" disabled={saving || total <= 0} data-testid="receipt-submit"
              className="px-5 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50 bg-[#28C878] text-black hover:bg-[#28C878]/90 transition-colors">
              <CheckCircle size={12} weight="fill" />
              {saving ? "Saving…" : sendImmediately ? `Send receipt · ${formatUSD(total)}` : `Save receipt · ${formatUSD(total)}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#28C878] outline-none" />
    </div>
  );
}

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
